import React, { useState } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
} from 'reactstrap'

const KeeperLoginModal = ({ isOpen, toggle, syncState, onStartSync, onSubmit2fa, onSubmitSsoToken }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [ssoToken, setSsoToken] = useState('')
  const [useSso, setUseSso] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const { status, prompt, ssoLoginUrl, progress, error, loading } = syncState

  const handleLoginSubmit = (e) => {
    e.preventDefault()
    if (email) {
      onStartSync(email, useSso ? null : password)
    }
  }

  const handle2faSubmit = (e) => {
    e.preventDefault()
    if (code && !submitting) {
      setSubmitting(true)
      onSubmit2fa(code)
      setCode('')
    }
  }

  const handleSsoTokenSubmit = (e) => {
    e.preventDefault()
    if (ssoToken && !submitting) {
      setSubmitting(true)
      onSubmitSsoToken(ssoToken)
      setSsoToken('')
    }
  }

  const handleClose = () => {
    setEmail('')
    setPassword('')
    setCode('')
    setSubmitting(false)
    setSsoToken('')
    toggle()
  }

  const renderLoginForm = () => (
    <Form onSubmit={handleLoginSubmit}>
      <FormGroup>
        <Label for="keeperEmail">Keeper Email</Label>
        <Input
          type="email"
          id="keeperEmail"
          placeholder="admin@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          required
        />
      </FormGroup>
      <FormGroup check className="mb-3">
        <Label check>
          <Input
            type="checkbox"
            checked={useSso}
            onChange={(e) => setUseSso(e.target.checked)}
            disabled={loading}
          />{' '}
          Use SSO (Entra / Azure AD)
        </Label>
      </FormGroup>
      {!useSso && (
        <FormGroup>
          <Label for="keeperPassword">Master Password</Label>
          <Input
            type="password"
            id="keeperPassword"
            placeholder="Enter master password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            required
          />
        </FormGroup>
      )}
      <Button color="primary" type="submit" disabled={loading || !email || (!useSso && !password)}>
        {loading ? <><Spinner size="sm" /> Connecting...</> : useSso ? 'Continue with SSO' : 'Sign In'}
      </Button>
    </Form>
  )

  const renderSsoFlow = () => (
    <div>
      <Alert color="info">
        <strong>SSO Authentication</strong>
        <ol className="mb-0 mt-2">
          <li>Click the button below to open the Keeper SSO login page</li>
          <li>Authenticate with your identity provider (Entra)</li>
          <li>On the Keeper SSO Connect page, click <strong>"Copy login token"</strong></li>
          <li>Paste the token below and click Submit</li>
        </ol>
      </Alert>
      <div className="mb-3">
        <Button
          color="primary"
          outline
          onClick={() => window.open(ssoLoginUrl, '_blank', 'width=600,height=700')}
        >
          Open Keeper SSO Login
        </Button>
      </div>
      <Form onSubmit={handleSsoTokenSubmit}>
        <FormGroup>
          <Label for="keeperSsoToken">SSO Login Token</Label>
          <Input
            type="textarea"
            id="keeperSsoToken"
            placeholder="Paste the token copied from the Keeper SSO Connect page"
            value={ssoToken}
            onChange={(e) => setSsoToken(e.target.value)}
            rows={3}
          />
        </FormGroup>
        <Button color="primary" type="submit" disabled={!ssoToken || submitting}>
          {submitting ? <><Spinner size="sm" /> Submitting...</> : 'Submit Token'}
        </Button>
      </Form>
    </div>
  )

  const render2faForm = () => (
    <Form onSubmit={handle2faSubmit}>
      <Alert color="info">{prompt || 'Enter your two-factor authentication code'}</Alert>
      <FormGroup>
        <Label for="keeper2fa">Verification Code</Label>
        <Input
          type="text"
          id="keeper2fa"
          placeholder="Enter code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          autoFocus
          required
        />
      </FormGroup>
      <Button color="primary" type="submit" disabled={!code || submitting}>
        {submitting ? <><Spinner size="sm" /> Submitting...</> : 'Submit Code'}
      </Button>
    </Form>
  )

  const renderSyncing = () => (
    <div className="text-center py-4">
      <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
      <p className="mt-3 mb-0">
        <strong>Pulling managed company data...</strong>
      </p>
      {progress && <p className="text-muted">{progress}</p>}
    </div>
  )

  const renderError = () => (
    <div>
      <Alert color="danger">{error || 'An error occurred'}</Alert>
      <Button color="primary" onClick={handleClose}>
        Close
      </Button>
    </div>
  )

  const renderContent = () => {
    switch (status) {
      case 'awaiting_sso':
        return renderSsoFlow()
      case 'awaiting_2fa':
        return render2faForm()
      case 'syncing':
      case 'authenticating':
        return renderSyncing()
      case 'error':
        return renderError()
      case 'complete':
        return (
          <div>
            <Alert color="success">Sync complete! Data has been loaded.</Alert>
            <Button color="primary" onClick={handleClose}>
              Close
            </Button>
          </div>
        )
      default:
        return renderLoginForm()
    }
  }

  const getTitle = () => {
    switch (status) {
      case 'awaiting_sso':
        return 'SSO Authentication'
      case 'awaiting_2fa':
        return 'Two-Factor Authentication'
      case 'syncing':
      case 'authenticating':
        return 'Syncing from Keeper...'
      case 'complete':
        return 'Sync Complete'
      case 'error':
        return 'Sync Error'
      default:
        return 'Sign in to Keeper'
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} backdrop="static">
      <ModalHeader toggle={handleClose}>{getTitle()}</ModalHeader>
      <ModalBody>{renderContent()}</ModalBody>
    </Modal>
  )
}

export default KeeperLoginModal
